import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Search } from "lucide-react";
import { Dropdown } from "primereact/dropdown";
import DateRangePicker from "./DateRangePicker";
import { useTranslation } from "react-i18next";
import BranchDropdown from "pages/Dashboard/Overview/BranchDropdown";
import { Slider } from "primereact/slider";
import { MultiSelect } from "primereact/multiselect";
import { percentageOptions } from "components/constants/DropdownOptions";

// In your AdvancedFilterDropdown component
const AdvancedFilterDropdown = ({
  filter,
  selectedFilters,
  handleFilterChange,
  t,
}) => {
  const [visible, setVisible] = useState(false);

  const handleOptionClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Dropdown
      value={null}
      options={[{ name: filter.placeholder }]}
      optionLabel="name"
      placeholder={filter.placeholder}
      className="custom_dropdown"
      panelClassName="custom_customer_filter"
      showClear={false}
      onShow={() => setVisible(true)}
      onHide={() => setVisible(false)}
      visible={visible}
      onClickOutside={() => setVisible(false)}
      itemTemplate={() => (
        <div className="advanced-filter-container" onClick={handleOptionClick}>
          {filter.filters.map((subFilter) => (
            <div key={subFilter.key} className="mb-3">
              <h6 className="fs_12 text_gray mb-2">{subFilter.name}</h6>

              {/* Checkbox Filter */}
              {subFilter.type === "checkbox" && (
                <div className="checkbox-group">
                  {subFilter.options.map((option) => (
                    <div
                      key={option.value}
                      className="custom-checkbox mb-1 d-flex gap-2"
                    >
                      <input
                        type="checkbox"
                        id={`${subFilter.key}-${option.value}`}
                        checked={
                          option.value === null
                            ? Array.isArray(selectedFilters[subFilter.key]) &&
                              selectedFilters[subFilter.key].length ===
                                subFilter.options.filter(
                                  (opt) => opt.value !== null
                                ).length
                            : Array.isArray(selectedFilters[subFilter.key]) &&
                              selectedFilters[subFilter.key].includes(
                                option.value
                              )
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          const current = selectedFilters[subFilter.key] || [];
                          if (option.value === null) {
                            const allValues = subFilter.options
                              .filter((opt) => opt.value !== null)
                              .map((opt) => opt.value);
                            const allSelected =
                              Array.isArray(current) &&
                              allValues.every((val) => current.includes(val));
                            handleFilterChange(
                              subFilter.key,
                              allSelected ? [] : allValues
                            );
                          } else {
                            if (current.includes(option.value)) {
                              const updated = current.filter(
                                (id) => id !== option.value
                              );
                              handleFilterChange(subFilter.key, updated);
                            } else {
                              handleFilterChange(subFilter.key, [
                                ...current,
                                option.value,
                              ]);
                            }
                          }
                        }}
                      />
                      <label htmlFor={`${subFilter.key}-${option.value}`}>
                        {option.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Button Filters (Radio type) */}
              {subFilter.type === "buttons" && (
                <div className="radio_buttons">
                  {subFilter.options.map((option) => (
                    <div
                      className="d-flex align-items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange(subFilter.key, option.value);
                      }}
                    >
                      <div
                        className={`circle ${
                          selectedFilters[subFilter.key] === option.value
                            ? "active"
                            : ""
                        }`}
                      ></div>
                      <button
                        key={option.value}
                        className={`transparent_btn fs_14 `}
                      >
                        {option.name}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Range Filter */}
              {subFilter.type === "range" && (
                <div
                  className="range-filter mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="d-flex justify-content-between mb-3 range_filter_inner">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Min"
                      value={selectedFilters[`${subFilter.key}Min`] ?? ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        // Check if the input is empty and set null, otherwise convert to number
                        const value =
                          e.target.value === "" ? null : Number(e.target.value);
                        handleFilterChange(`${subFilter.key}Min`, value);
                      }}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm ml-2"
                      placeholder="Max"
                      value={selectedFilters[`${subFilter.key}Max`] ?? ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        // Check if the input is empty and set null, otherwise convert to number
                        const value =
                          e.target.value === "" ? null : Number(e.target.value);
                        handleFilterChange(`${subFilter.key}Max`, value);
                      }}
                    />
                  </div>
                  <Slider
                    value={[
                      selectedFilters[`${subFilter.key}Min`] !== null &&
                      selectedFilters[`${subFilter.key}Min`] !== undefined
                        ? selectedFilters[`${subFilter.key}Min`]
                        : subFilter.defaultMin || 0,
                      selectedFilters[`${subFilter.key}Max`] !== null &&
                      selectedFilters[`${subFilter.key}Max`] !== undefined
                        ? selectedFilters[`${subFilter.key}Max`]
                        : subFilter.defaultMax || 100,
                    ]}
                    onChange={(e) => {
                      // PrimeReact Slider returns a different event structure
                      if (e.originalEvent && e.originalEvent.stopPropagation) {
                        e.originalEvent.stopPropagation();
                      }
                      handleFilterChange(`${subFilter.key}Min`, e.value[0]);
                      handleFilterChange(`${subFilter.key}Max`, e.value[1]);
                    }}
                    range
                    min={subFilter.min || 0}
                    max={subFilter.max || 1000}
                    step={subFilter.step || 1}
                  />
                </div>
              )}
              {/* Discount Type: Fixed or Input */}
              {subFilter.key === "discountType" && (
                <div className="discount-type mt-2">
                  {selectedFilters.discountType === "percentage" ? (
                    <select
                      className="form-control"
                      value={selectedFilters.discountValue || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleFilterChange("discountValue", e.target.value);
                      }}
                    >
                      <option value="">Discount Value</option>
                      {percentageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Discount Value"
                      value={selectedFilters.discountValue || ""}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleFilterChange("discountValue", e.target.value);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            className="transparent_btn d-block ms-auto text_primary fs_14 mt-2"
            onClick={(e) => {
              e.stopPropagation();
              filter.filters.forEach((subFilter) => {
                if (subFilter.type === "checkbox") {
                  handleFilterChange(subFilter.key, []);
                } else if (subFilter.type === "buttons") {
                  handleFilterChange(subFilter.key, null);
                } else if (subFilter.type === "range") {
                  handleFilterChange(`${subFilter.key}Min`, null);
                  handleFilterChange(`${subFilter.key}Max`, null);
                } else if (subFilter.type === "number") {
                  handleFilterChange(subFilter.key, null);
                }
              });
            }}
          >
            {t("Reset")}
          </button>
        </div>
      )}
    />
  );
};

const FiltersWithoutSearch = ({
  keyword,
  setKeyword,
  selectedFilters = {},
  setSelectedFilters = () => {},
  dates = [],
  setDates = () => {},
  filters = [],
  onSearch = () => {},
  onReset = () => {},
  isDateRange = true,
  showSearchButton = true,
  isBtn = false,
  btnText = "",
  onBtnClick = () => {},
  className = "",
}) => {
  const { t } = useTranslation();

  const { branchesWithCity } = useSelector((state) => state.Branches);

  const handleFilterChange = (key, value) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    console.log(key,value)
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setKeyword(value);

    // Call handleSearch when input becomes empty
    if (value.trim() === "") {
      setTimeout(() => {
        onSearch(null, { actionType: "RESET_KEYWORD" });
      }, 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearch(1, { actionType: "SEARCH" });
    }
  };

  const handleReset = () => {
    onReset();
  };

  const handleBranchApply = () => {
    onSearch(1, { actionType: "SEARCH" });
  };

  const handleBranchSelectionChange = (newSelected) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      branches: newSelected,
    }));
  };

  return (
    <Row className={`common_filter ${className}`}>
      <Col style={{ maxWidth: 441 }}>
        <div className="mt-2">
          <div className="d-flex align-items-center gap-3">

          </div>
        </div>
      </Col>
      <Col>
        <div className="g_end flex-wrap gap-3 mt-2">
          {filters.map((filter) =>
            filter.type === "branchCityFilter" ? (
              <BranchDropdown
                key={filter.key}
                data={branchesWithCity}
                label={t("Branch")}
                allLabel={t("Choose All Branches")}
                resetLabel={t("Reset all")}
                onSelectionChange={handleBranchSelectionChange}
                selectedData={selectedFilters.branches}
                handleApply={handleBranchApply}
              />
            ) : filter.type === "singleSelect" ? (
              <Dropdown
                key={filter.key}
                value={selectedFilters[filter.key]}
                onChange={(e) => {
                  handleFilterChange(filter.key, e.value);
                }}
                options={filter.options}
                optionLabel="name"
                placeholder={filter.placeholder}
                className="custom_dropdown"
              />
            ) : filter.type === "multiSelect" ? (
              <MultiSelect
                key={filter.key}
                value={selectedFilters[filter.key]}
                onChange={(e) => {
                  handleFilterChange(filter.key, e.value);
                }}
                options={filter.options}
                optionLabel="name"
                selectAllLabel={t("Select All")}
                placeholder={filter.placeholder}
                className="filter_multi_select"
              />
            ) : filter.type === "advancedFilter" ? (
              <AdvancedFilterDropdown
                key={filter.key}
                filter={filter}
                selectedFilters={selectedFilters}
                handleFilterChange={handleFilterChange}
                t={t}
              />
            ) : (
              ""
            )
          )}
          {isDateRange && <DateRangePicker dates={dates} setDates={setDates} />}
            {showSearchButton && 
            <div className="buttons">
              <button
                className="search_btn"
                onClick={() => onSearch(1, { actionType: "SEARCH" })}
              >
                {t("Search")}
              </button>
              <button className="reset_btn" onClick={handleReset}>
                {t("Reset")}
              </button>
            </div>
            }
        </div>
      </Col>
    </Row>
  );
};

export default FiltersWithoutSearch;
